/**
 * Pricing / Membership page — Feb 2026 v4 rebuild.
 *
 * Alignment with the TBC Founding Business master spec:
 *   - Base Plan   $0 forever (list + inquiry)
 *   - Growth      $9.99/mo — 30-day free trial (backend now honors it)
 *   - Accelerator $19.99/mo — 30-day free trial, 2.5% fee
 *   - Founding    $79.99 ONE TIME (permanent status, first 100 only)
 *
 * Founder card is now state-driven from GET /founder/status +
 * GET /founder/eligibility, so the CTA correctly reflects:
 *   - already-founder     → badge + "view benefits"
 *   - eligible non-founder → live remaining count + $79.99 CTA
 *   - ineligible          → explanatory copy, no purchase CTA
 *   - cap reached         → "Founding Business Membership Is Closed"
 *
 * Mobile bug fixed: the `-mt-14` overlap into the dark hero was clipping
 * the Base Plan header. We now use `relative z-10` on the plans grid and
 * shrunk the overlap on small screens.
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  Check, X as XIcon, Star, ArrowRight, Loader2, Gift, Trophy,
  ShieldCheck, RefreshCcw, DollarSign, TrendingUp, Zap, Users,
  Search, Heart, ShoppingBag, Calendar, Tag, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { API, useAuth } from "@/App";

// -------------------- Base Plan --------------------
const FREE_INCLUDED = [
  "Official business listing",
  "Up to 2 products (catalog only)",
  "Inquiry button — buyers message you directly",
];
const FREE_EXCLUDED = ["Storefront checkout", "Analytics"];

// -------------------- Growth --------------------
const GROWTH_FEATURES = [
  "30-day free trial — no charge for the first month",
  "Verified business listing",
  "Up to 20 products",
  "Full storefront with direct checkout",
  "Basic analytics dashboard",
  "4% platform fee per transaction",
];

// -------------------- Accelerator --------------------
const ACCELERATOR_FEATURES = [
  "30-day free trial — no charge for the first month",
  "Everything in Growth, plus…",
  "Up to 40 products",
  "Featured placement in search + category pages",
  "Full analytics dashboard",
  "Reduced platform fee — 2.5% per transaction",
  "Priority customer support",
];

// -------------------- Founding perks --------------------
// Financial-first ordering per master spec section 10 / 35.
const FOUNDING_BENEFITS = [
  { icon: DollarSign, text: "20% OFF eligible paid TBC Business Plans for life" },
  { icon: TrendingUp, text: "20% OFF eligible TBC-owned marketing & advertising services for life" },
  { icon: Gift, text: "Additional 30 days FREE on your first eligible paid Business Plan — 60 days free total" },
  { icon: Tag, text: "Exclusive Founder discounts on eligible TBC merchandise, services & partner offers" },
  { icon: Trophy, text: "Permanent Founding Business badge" },
  { icon: Star, text: "Permanent recognition as one of TBC's first 100 businesses" },
  { icon: Zap, text: "Priority consideration for eligible TBC promotions & campaigns" },
  { icon: Users, text: "Founder networking and promotional opportunities" },
  { icon: Sparkles, text: "Direct input on new TBC features and platform improvements" },
];

// -------------------- Community perks --------------------
const COMMUNITY_ITEMS = [
  { icon: Search, title: "Discover & Support", desc: "Find and support Black-owned businesses." },
  { icon: Heart, title: "Save & Follow", desc: "Save your favorite businesses and get updates." },
  { icon: ShoppingBag, title: "Shop the Marketplace", desc: "Buy products and services from our community." },
  { icon: Calendar, title: "Events & News", desc: "Stay up to date on events, news, and opportunities." },
  { icon: Tag, title: "Exclusive Offers", desc: "Get early access to deals, giveaways, and more." },
];

export default function PricingPage() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [founderStatus, setFounderStatus] = useState(null);      // public availability
  const [founderEligibility, setFounderEligibility] = useState(null); // per-user

  // Pre-founder-checkout offer prompt (before Growth/Accelerator activation)
  const [pendingPlan, setPendingPlan] = useState(null); // { tier, planLabel }

  const loadPricing = useCallback(async () => {
    try {
      const [status, plans] = await Promise.all([
        axios.get(`${API}/founder/status`),
        axios.get(`${API}/subscription/plans`).catch(() => null),
      ]);
      setFounderStatus(status.data);
      if (user) {
        try {
          const elig = await axios.get(`${API}/founder/eligibility`, { withCredentials: true });
          setFounderEligibility(elig.data);
        } catch {
          setFounderEligibility(null);
        }
      }
      // plans unused for now — subscription/plans call keeps the price cache warm
      void plans;
    } catch {
      toast.error("Couldn't load pricing");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    document.title = "Business Plans — The Blk Community";
    setLoading(true);
    loadPricing();
  }, [loadPricing]);

  // Regular plan checkout (Growth or Accelerator) — no Founder bundle
  const startRegularCheckout = useCallback(async (tier) => {
    if (!user) {
      toast.info("Sign in first to upgrade");
      login();
      return;
    }
    try {
      const myBiz = await axios.get(`${API}/my-business`, { withCredentials: true });
      const business = myBiz.data?.business;
      if (!business) {
        toast.info("Create your business listing first", {
          description: "You need an active business profile to upgrade.",
          action: { label: "Create", onClick: () => navigate("/dashboard") },
        });
        return;
      }
      setCheckoutLoading(tier);
      const res = await axios.post(
        `${API}/subscription/checkout`,
        {
          business_id: business.business_id,
          period: "monthly",
          tier,
          origin_url: window.location.origin,
        },
        { withCredentials: true },
      );
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error("Couldn't start checkout");
        setCheckoutLoading(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start checkout");
      setCheckoutLoading(null);
    }
  }, [user, login, navigate]);

  // Bundled Founder + first-paid-plan checkout
  const startFounderCheckout = useCallback(async (planTier) => {
    if (!user) {
      toast.info("Sign in first to become a Founder");
      login();
      return;
    }
    try {
      const myBiz = await axios.get(`${API}/my-business`, { withCredentials: true });
      const business = myBiz.data?.business;
      if (!business) {
        toast.info("Create your business listing first", {
          action: { label: "Create", onClick: () => navigate("/dashboard") },
        });
        return;
      }
      setCheckoutLoading("founder");
      const res = await axios.post(
        `${API}/founder/checkout`,
        {
          business_id: business.business_id,
          plan_tier: planTier,
          plan_period: "monthly",
          origin_url: window.location.origin,
        },
        { withCredentials: true },
      );
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error("Couldn't start Founder checkout");
        setCheckoutLoading(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start Founder checkout");
      setCheckoutLoading(null);
    }
  }, [user, login, navigate]);

  // When a user picks Growth or Accelerator: if they're Founder-eligible,
  // show the interstitial offer FIRST (spec section 5). Otherwise go direct.
  const handleUpgrade = useCallback((tier) => {
    const eligible = founderEligibility?.eligible;
    if (eligible) {
      setPendingPlan({
        tier,
        planLabel: tier === "premium" ? "Accelerator" : "Growth",
      });
    } else {
      startRegularCheckout(tier);
    }
  }, [founderEligibility, startRegularCheckout]);

  const scrollToFounding = () => {
    document.getElementById("founding-card")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // Founder card state resolution
  const founderRemaining = founderStatus?.remaining ?? 100;
  const founderClaimed = founderStatus?.claimed ?? 0;
  const capReached = founderStatus && !founderStatus.available;
  const isAlreadyFounder = founderEligibility?.is_founding_member;
  const eligibleReason = founderEligibility?.reason;

  return (
    <div className="min-h-screen bg-tbc-cream">
      <Navbar />

      {/* ================= HERO ================= */}
      <section
        className="bg-tbc-navy text-white pt-14 sm:pt-20 pb-16 sm:pb-24 px-4 relative"
        data-testid="pricing-hero"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-start">
            <div className="text-center lg:text-left">
              <h1
                className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-tight"
                data-testid="pricing-hero-heading"
              >
                Business <span className="text-tbc-gold">Plans</span>
              </h1>
              <p className="text-white/80 text-lg mt-4 max-w-2xl">
                Get discovered for free. Upgrade when you&apos;re ready to sell and grow.
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/70 justify-center lg:justify-start">
                <span className="inline-flex items-center gap-1.5">
                  <Check className="w-4 h-4 text-tbc-gold" /> 30-day free trial on any paid plan
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Check className="w-4 h-4 text-tbc-gold" /> Cancel anytime
                </span>
              </div>
            </div>

            {/* Founding pitch tile (pinned top-right on desktop) */}
            <div className="w-full lg:w-80 rounded-2xl border border-tbc-gold/40 bg-tbc-navy/60 backdrop-blur-sm hover:border-tbc-gold transition-colors group overflow-hidden">
              <button
                type="button"
                onClick={() => navigate("/founding-business")}
                className="w-full text-left px-5 pt-4 pb-2 focus:outline-none"
                data-testid="founding-hero-callout"
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-tbc-gold/15 flex items-center justify-center flex-none">
                    <Star className="w-4 h-4 text-tbc-gold fill-tbc-gold" />
                  </div>
                  <div>
                    <p className="text-tbc-gold text-xs uppercase tracking-wider font-semibold">
                      {isAlreadyFounder ? "Founding Business" : "Founding Business?"}
                    </p>
                    <p className="text-sm text-white/85 mt-1 leading-relaxed">
                      {isAlreadyFounder ? (
                        <>Your Founder benefits stay with you for life — <strong className="text-tbc-gold">20% off</strong> eligible paid plans + permanent badge.</>
                      ) : capReached ? (
                        <>All 100 Founding Business positions have been claimed.</>
                      ) : (
                        <><strong className="text-tbc-gold">$79.99 once.</strong> Only {founderRemaining} of 100 spots remain.</>
                      )}
                    </p>
                    <span className="inline-flex items-center gap-1 text-xs text-tbc-gold mt-2 group-hover:gap-2 transition-all">
                      Learn more <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </button>
              <div className="px-5 pb-4 pl-[4.25rem]">
                <Link
                  to="/founding-100"
                  className="inline-flex items-center gap-1 text-[11px] text-white/70 hover:text-tbc-gold underline underline-offset-2 transition-colors"
                  data-testid="pricing-founding-100-link"
                >
                  View the Founding 100 ledger
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= 4 PLAN CARDS ================= */}
      {/*
        Mobile-overlap fix (spec section 36-38):
          * relative + z-10 so the plan cards ALWAYS sit above the dark hero,
            regardless of how tall the Founding callout renders.
          * Reduced negative overlap on mobile (-mt-6) so the Base Plan
            heading never gets clipped by the hero background.
      */}
      <section className="px-4 -mt-6 sm:-mt-10 pb-14 relative z-10">
        <div className="max-w-7xl mx-auto grid gap-5 md:grid-cols-2 lg:grid-cols-4 items-stretch">

          {/* --- Base Plan (Free) --- */}
          <div
            className="bg-white rounded-2xl border border-tbc-navy/10 p-6 flex flex-col shadow-sm"
            data-testid="plan-free"
          >
            <p className="text-xs tracking-[0.2em] uppercase text-tbc-navy/50 font-medium">List</p>
            <h3 className="font-heading text-2xl text-tbc-navy mt-1">Base Plan</h3>
            <p className="text-xs tracking-wider uppercase text-tbc-navy/50 mt-3 font-medium">Free Forever</p>
            <p className="mt-2">
              <span className="font-heading text-4xl text-tbc-navy">$0</span>
              <span className="text-tbc-navy/60 ml-2 text-sm">forever</span>
            </p>
            <p className="mt-3 text-sm text-tbc-navy/70 leading-snug">
              Get listed and start getting inquiries. Perfect for testing the waters.
            </p>
            <div className="my-5 border-t border-tbc-navy/10" />
            <ul className="space-y-3 flex-1">
              {FREE_INCLUDED.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-tbc-navy/85">
                  <Check className="w-4 h-4 text-tbc-gold mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
              {FREE_EXCLUDED.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-tbc-navy/40 line-through">
                  <XIcon className="w-4 h-4 text-tbc-navy/25 mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Button
              variant="outline"
              className="mt-5 rounded-full border-tbc-navy/20 text-tbc-navy hover:bg-tbc-navy hover:text-white"
              onClick={() => navigate(user ? "/dashboard" : "/join")}
              data-testid="plan-free-cta"
            >
              {user ? "Go to Dashboard" : "Get Started Free"}
            </Button>
            <p className="mt-2 text-[11px] text-center text-tbc-navy/50">No credit card required</p>
          </div>

          {/* --- Growth --- */}
          <div
            className="relative bg-white rounded-2xl border-2 border-tbc-navy p-6 flex flex-col shadow-md"
            data-testid="plan-standard"
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-tbc-navy text-white text-[10px] font-bold tracking-wider uppercase flex items-center gap-1 whitespace-nowrap">
              <Gift className="w-3 h-3" />
              First 30 Days Free
            </div>
            <p className="text-xs tracking-[0.2em] uppercase text-tbc-navy/60 font-medium">Sell</p>
            <h3 className="font-heading text-2xl text-tbc-navy mt-1">Growth</h3>
            <p className="mt-4">
              <span className="font-heading text-4xl text-tbc-navy">$9.99</span>
              <span className="text-tbc-navy/60 ml-2 text-sm">/month</span>
            </p>
            <p className="mt-3 text-sm text-tbc-navy/70 leading-snug">
              Start selling with a full storefront and basic analytics.
            </p>
            <div className="my-5 border-t border-tbc-navy/10" />
            <ul className="space-y-3 flex-1">
              {GROWTH_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-tbc-navy/85">
                  <Check className="w-4 h-4 text-tbc-gold mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Button
              onClick={() => handleUpgrade("standard")}
              disabled={checkoutLoading === "standard"}
              className="mt-5 rounded-full bg-tbc-navy hover:bg-tbc-navy/90 text-white font-medium"
              data-testid="plan-standard-cta"
            >
              {checkoutLoading === "standard" ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting…</>
              ) : (
                <>Start Free for 30 Days <ArrowRight className="w-4 h-4 ml-2" /></>
              )}
            </Button>
            <p className="mt-2 text-[11px] text-center text-tbc-navy/50">Cancel anytime.</p>
          </div>

          {/* --- Accelerator (BEST VALUE, dark card) --- */}
          <div
            className="relative bg-gradient-to-br from-tbc-navy to-[#0F2440] rounded-2xl p-6 flex flex-col text-white shadow-xl ring-2 ring-tbc-gold"
            data-testid="plan-premium"
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-tbc-gold text-tbc-navy text-[10px] font-bold tracking-wider uppercase flex items-center gap-1 whitespace-nowrap">
              <Star className="w-3 h-3 fill-tbc-navy" />
              Best Value
            </div>
            <p className="text-xs tracking-[0.2em] uppercase text-tbc-gold font-medium">Grow</p>
            <h3 className="font-heading text-2xl text-white mt-1">Accelerator</h3>
            <p className="mt-4">
              <span className="font-heading text-4xl text-tbc-gold">$19.99</span>
              <span className="text-white/70 ml-2 text-sm">/month</span>
            </p>
            <p className="mt-3 text-sm text-white/85 leading-snug">
              Built for businesses serious about growth.
            </p>
            <div className="my-5 border-t border-white/10" />
            <ul className="space-y-3 flex-1">
              {ACCELERATOR_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-white/90">
                  <Check className="w-4 h-4 text-tbc-gold mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Button
              onClick={() => handleUpgrade("premium")}
              disabled={checkoutLoading === "premium"}
              className="mt-4 rounded-full bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy font-semibold"
              data-testid="plan-premium-cta"
            >
              {checkoutLoading === "premium" ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting…</>
              ) : (
                <>Start Free for 30 Days <ArrowRight className="w-4 h-4 ml-2" /></>
              )}
            </Button>
            <p className="mt-2 text-[11px] text-center text-white/60">Cancel anytime.</p>
          </div>

          {/* --- Founding Business --- */}
          <FounderCard
            capReached={capReached}
            claimed={founderClaimed}
            remaining={founderRemaining}
            isAlreadyFounder={isAlreadyFounder}
            eligibility={founderEligibility}
            eligibleReason={eligibleReason}
            checkoutLoading={checkoutLoading}
            onFounderStart={() => setPendingPlan({ tier: "premium", planLabel: "Accelerator" })}
            onSignIn={login}
            user={user}
            navigate={navigate}
          />
        </div>
      </section>

      {/* Founder-offer interstitial */}
      {pendingPlan && (
        <FounderInterstitial
          plan={pendingPlan}
          founderStatus={founderStatus}
          checkoutLoading={checkoutLoading}
          onAccept={() => startFounderCheckout(pendingPlan.tier)}
          onDecline={() => {
            startRegularCheckout(pendingPlan.tier);
            setPendingPlan(null);
          }}
          onClose={() => setPendingPlan(null)}
        />
      )}

      {/* ================= NOT A BUSINESS? ================= */}
      <section className="px-4 pb-14">
        <div className="max-w-7xl mx-auto bg-white rounded-2xl border border-tbc-navy/10 p-6 sm:p-8 shadow-sm" data-testid="community-cta">
          <div className="grid lg:grid-cols-[300px_1fr_auto] gap-6 items-center">
            <div>
              <p className="text-xs tracking-wider uppercase text-tbc-navy/50 font-medium">
                Not a business?
              </p>
              <h3 className="font-heading text-2xl text-tbc-navy mt-1">
                Join the community for free.
              </h3>
              <p className="text-sm text-tbc-navy/70 mt-2 leading-snug">
                Create a free account to follow and support Black-owned businesses, leave reviews, shop the marketplace, and stay connected with events and offers.
              </p>
              <Button
                onClick={() => navigate(user ? "/directory" : "/join")}
                className="mt-4 rounded-full bg-tbc-navy hover:bg-tbc-navy/90 text-white lg:hidden"
                data-testid="community-cta-btn-mobile"
              >
                Join the Community
              </Button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {COMMUNITY_ITEMS.map(({ icon: Icon, title, desc }) => (
                <div key={title} className="text-center">
                  <div className="w-12 h-12 rounded-full bg-tbc-cream/70 flex items-center justify-center mx-auto">
                    <Icon className="w-5 h-5 text-tbc-navy" />
                  </div>
                  <p className="font-heading text-sm text-tbc-navy mt-2">{title}</p>
                  <p className="text-[11px] text-tbc-navy/60 mt-1 leading-snug">{desc}</p>
                </div>
              ))}
            </div>

            <Button
              onClick={() => navigate(user ? "/directory" : "/join")}
              className="hidden lg:inline-flex rounded-full bg-tbc-navy hover:bg-tbc-navy/90 text-white px-6"
              data-testid="community-cta-btn"
            >
              Join the Community
            </Button>
          </div>
        </div>
      </section>

      {/* ================= TRUST STRIP ================= */}
      <section className="bg-tbc-navy text-white/80 px-4 py-4" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-center items-center gap-x-8 gap-y-2 text-xs">
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-tbc-gold" /> Secure payments
          </span>
          <span className="inline-flex items-center gap-1.5">
            <RefreshCcw className="w-4 h-4 text-tbc-gold" /> Cancel anytime
          </span>
          <span className="inline-flex items-center gap-1.5">
            <DollarSign className="w-4 h-4 text-tbc-gold" /> Transparent pricing
          </span>
          <span className="inline-flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4 text-tbc-gold" /> We grow when you grow
          </span>
        </div>
      </section>

      <Footer />

      {loading && (
        <div className="fixed bottom-4 right-4 text-xs text-tbc-navy/60 flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading pricing…
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------------------
// Founder card — 4 visual states driven by eligibility + status
// -------------------------------------------------------------------------
function FounderCard({
  capReached,
  claimed,
  remaining,
  isAlreadyFounder,
  eligibility,
  eligibleReason,
  checkoutLoading,
  onFounderStart,
  onSignIn,
  user,
}) {
  // Determine which visual state to render.
  //   1. Already Founder (spec section 32)
  //   2. Cap reached (section 21)
  //   3. Ineligible prior subscriber (section 34)
  //   4. Eligible non-Founder (section 33)
  //   5. Not signed in (defaults to state 4 with sign-in CTA)

  const canPurchase =
    !isAlreadyFounder && !capReached &&
    (!user || eligibleReason !== "already_activated_paid");

  return (
    <div
      id="founding-card"
      className="relative bg-tbc-navy rounded-2xl p-6 flex flex-col text-white shadow-xl border border-tbc-gold/40"
      data-testid="plan-founding"
    >
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-full bg-tbc-gold/15 flex items-center justify-center flex-none">
          <Users className="w-4 h-4 text-tbc-gold" />
        </div>
        <div>
          <p className="text-xs tracking-[0.2em] uppercase text-tbc-gold font-medium">
            {isAlreadyFounder ? "Your Status" : "Become a"}
          </p>
          <h3 className="font-heading text-lg text-white leading-tight">
            {isAlreadyFounder ? "Founding Business" : "Founding Business"}
          </h3>
        </div>
      </div>

      {isAlreadyFounder ? (
        <>
          <p className="mt-4 text-sm text-white/90 leading-snug" data-testid="founder-state-already">
            🏆{" "}
            <strong className="text-tbc-gold">
              {eligibility?.founding_member_number
                ? `You secured Founding Business #${eligibility.founding_member_number}`
                : "You are a Founding Business"}
            </strong>
            . Your Founder benefits stay with you for life — including your permanent Founding Business badge and 20% off eligible paid TBC Business Plans.
          </p>
        </>
      ) : capReached ? (
        <>
          <p className="mt-4 text-sm text-white/90 leading-snug" data-testid="founder-state-cap">
            <strong className="text-tbc-gold">Founding Business Membership Is Closed.</strong> All 100 Founding Business positions have been claimed.
          </p>
          <p className="mt-2 text-xs text-white/70 leading-relaxed">
            Regular Business Plans remain available. Existing Founders retain their benefits permanently.
          </p>
        </>
      ) : eligibleReason === "already_activated_paid" ? (
        <>
          <p className="mt-4 text-sm text-white/90 leading-snug" data-testid="founder-state-ineligible">
            Founding Business enrollment is offered when you activate your <strong className="text-tbc-gold">first paid TBC Business Plan</strong>.
          </p>
          <p className="mt-2 text-xs text-white/70 leading-relaxed">
            Because your business has already activated a paid plan, self-service Founding Business enrollment isn&apos;t available. Contact support if you believe this is an error.
          </p>
        </>
      ) : (
        <>
          <p className="mt-4 text-sm text-white/90 leading-snug" data-testid="founder-state-eligible">
            Join the first <strong className="text-tbc-gold">100 Founding Businesses</strong> and unlock permanent recognition + exclusive lifetime benefits.
          </p>
          <div className="mt-3 flex items-baseline gap-1.5">
            <span className="font-heading text-3xl text-tbc-gold">$79.99</span>
            <span className="text-xs text-white/70">once · founder status for life</span>
          </div>
          <p className="mt-1 text-[11px] text-tbc-gold/80 uppercase tracking-wider">
            🔥 Only {remaining} of 100 spots remain · {claimed} claimed
          </p>
        </>
      )}

      <div className="mt-5 rounded-xl border border-white/10 bg-white/5 px-3 py-3 flex-1">
        <p className="text-[10px] uppercase tracking-wider text-tbc-gold font-semibold mb-2">
          Founding Business Benefits
        </p>
        <ul className="space-y-2">
          {FOUNDING_BENEFITS.map(({ icon: Icon, text }) => (
            <li key={text} className="flex items-start gap-2 text-xs text-white/85 leading-relaxed">
              <Icon className="w-3.5 h-3.5 text-tbc-gold mt-0.5 flex-shrink-0" />
              <span>{text}</span>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-[10px] text-white/50 leading-relaxed">
          20% off applies to eligible TBC Business Plans and eligible TBC-owned marketing services. Taxes, transaction/platform fees, ad spend, third-party costs, and certain promotions may be excluded.
        </p>
      </div>

      {isAlreadyFounder ? (
        <Button
          onClick={() => window.location.href = "/dashboard"}
          className="mt-5 rounded-full bg-tbc-gold text-tbc-navy hover:bg-tbc-gold/90 font-semibold"
          data-testid="founder-view-benefits-cta"
        >
          View Your Founder Benefits <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      ) : canPurchase ? (
        <Button
          onClick={() => user ? onFounderStart() : onSignIn()}
          disabled={checkoutLoading === "founder"}
          className="mt-5 rounded-full bg-tbc-gold text-tbc-navy hover:bg-tbc-gold/90 font-semibold"
          data-testid="founder-purchase-cta"
        >
          {checkoutLoading === "founder" ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting…</>
          ) : (
            <>Become a Founding Business — $79.99</>
          )}
        </Button>
      ) : null}
      <p className="mt-2 text-[11px] text-center text-white/60">
        {isAlreadyFounder
          ? "Founder status is permanent — even if you cancel your subscription."
          : "Only 100 spots will ever be available."}
      </p>
    </div>
  );
}

// -------------------------------------------------------------------------
// Founder-offer interstitial (spec section 5, 25)
// -------------------------------------------------------------------------
function FounderInterstitial({ plan, founderStatus, checkoutLoading, onAccept, onDecline, onClose }) {
  const remaining = founderStatus?.remaining ?? 100;
  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm px-3 pb-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      data-testid="founder-interstitial"
    >
      <div className="w-full max-w-lg bg-white rounded-2xl overflow-hidden shadow-2xl max-h-[95vh] overflow-y-auto">
        <div className="bg-tbc-navy text-white p-6 relative">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 text-white/60 hover:text-white"
            aria-label="Close"
            data-testid="founder-interstitial-close"
          >
            <XIcon className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Star className="w-4 h-4 text-tbc-gold fill-tbc-gold" />
            <p className="text-tbc-gold text-xs uppercase tracking-wider font-semibold">
              Founding Business Offer
            </p>
          </div>
          <h3 className="font-heading text-2xl mt-2">
            Become a Founding Business — <span className="text-tbc-gold">$79.99</span> once
          </h3>
          <p className="text-sm text-white/80 mt-2 leading-snug">
            Join TBC&apos;s first 100 Founding Businesses. Only <strong className="text-tbc-gold">{remaining}</strong> spots remain.
          </p>
        </div>
        <div className="p-6 space-y-4">
          <ul className="space-y-2 text-sm text-tbc-navy/85">
            <li className="flex items-start gap-2"><DollarSign className="w-4 h-4 text-tbc-gold mt-0.5" /> <span><strong>20% OFF</strong> eligible paid TBC Business Plans for life</span></li>
            <li className="flex items-start gap-2"><TrendingUp className="w-4 h-4 text-tbc-gold mt-0.5" /> <span><strong>20% OFF</strong> eligible TBC-owned marketing services for life</span></li>
            <li className="flex items-start gap-2"><Gift className="w-4 h-4 text-tbc-gold mt-0.5" /> <span><strong>Additional 30 days FREE</strong> — 60 days free total on {plan.planLabel}</span></li>
            <li className="flex items-start gap-2"><Trophy className="w-4 h-4 text-tbc-gold mt-0.5" /> <span>Permanent Founding Business badge & recognition</span></li>
          </ul>
          <div className="rounded-xl border border-tbc-navy/10 bg-tbc-cream/40 p-3 text-xs text-tbc-navy/80 leading-relaxed space-y-1">
            <div className="flex justify-between"><span>Due today</span><strong>$79.99</strong></div>
            <div className="flex justify-between"><span>{plan.planLabel} — first 60 days</span><strong>$0</strong></div>
            <div className="flex justify-between"><span>Then {plan.planLabel} with 20% Founder discount</span><strong>{plan.tier === "premium" ? "$15.99/mo" : "$7.99/mo"}</strong></div>
            <p className="pt-2 text-[11px] text-tbc-navy/60">Cancel the recurring plan anytime. Founder status is permanent.</p>
          </div>
          <div className="flex flex-col gap-2">
            <Button
              onClick={onAccept}
              disabled={checkoutLoading === "founder"}
              className="w-full rounded-full bg-tbc-gold text-tbc-navy hover:bg-tbc-gold/90 font-semibold whitespace-normal h-auto py-3 text-sm sm:text-base"
              data-testid="founder-interstitial-accept"
            >
              {checkoutLoading === "founder" ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting…</>
              ) : (
                <>Add Founding Business — $79.99</>
              )}
            </Button>
            <Button
              onClick={onDecline}
              disabled={checkoutLoading === "founder"}
              variant="outline"
              className="w-full rounded-full border-tbc-navy/20 text-tbc-navy hover:bg-tbc-navy hover:text-white whitespace-normal h-auto py-3 text-sm sm:text-base"
              data-testid="founder-interstitial-decline"
            >
              Continue Without Founder Status
            </Button>
          </div>
          <p className="text-[11px] text-tbc-navy/50 text-center leading-relaxed">
            Choosing &quot;Continue Without Founder Status&quot; means you may not become a Founder later, even if positions remain.
          </p>
        </div>
      </div>
    </div>
  );
}
